import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaBox,
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaCloudUploadAlt,
  FaComments,
  FaFacebook,
  FaInfoCircle,
  FaLightbulb,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaRupeeSign,
  FaShieldAlt,
  FaStar,
  FaTag,
  FaTimes,
  FaTwitter,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import MarketplaceProductShareButton from "../../components/marketplace/MarketplaceProductShareButton";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import CommonService from "../../services/common.service";
import MarketplaceAddToCartButton from "../../components/marketplace/MarketplaceAddToCartButton";
import StorageService from "../../services/StorageService";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { getMarketplaceEngagementNotice } from "../../utils/marketplaceProductEngagement";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/product_marketplace_detail.scss";
import "../../assets/styles/explore_product_categories.scss";

const TABS = [
  { id: "description", label: "Product Description" },
  { id: "lead_time", label: "Lead Time" },
  { id: "inventory", label: "Inventory Details" },
  { id: "company", label: "Company Details" },
];

const COMPANY_MAIN_TABS = [
  "Company Details",
  "Factory Details",
  "Quality Control",
  "Packaging Details",
];

const COMPANY_DETAILS_NAV = [
  "Company Overview",
  "Bank details",
  "Trading Information",
];

const FACTORY_DETAILS_NAV = [
  "Factory Details",
  "Warehouse Location",
  "Supply Chain Logistics",
];

const QUALITY_CONTROL_NAV = [
  "Quality Control",
  "Certificates",
];

const PACKAGING_DETAILS_NAV = [
  "Shipping Information",
  "Accepted Payment Methods",
  "Refund Policy",
];

const DEFAULT_PRODUCT_FAQ = [
  {
    question: "Wonder if you accept small orders?",
    answer: "Please contact the supplier to confirm MOQ and whether smaller trial orders are accepted.",
  },
  {
    question: "Are you a factory or a trading company?",
    answer: "Refer to the supplier company profile for business type and manufacturing details.",
  },
  {
    question: "Where is your factory located? Can I visit?",
    answer: "Factory and warehouse location details are available under Company Details on this page.",
  },
  {
    question: "How many years have you been in business?",
    answer: "Year of establishment and company overview are listed in the supplier profile.",
  },
  {
    question: "Can I have my own logo on the products?",
    answer: "Check the Supplier's Customization Ability section for logo and branding options.",
  },
];

const DEFAULT_INQUIRY_MESSAGE = "Please share the best price including delivery and GST.";

const parsePriceNumber = (value) => {
  const n = parseFloat(String(value || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const leadTimeDaysFromDate = (dateStr) => {
  if (!dateStr) return null;
  const delivery = new Date(dateStr);
  if (Number.isNaN(delivery.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  delivery.setHours(0, 0, 0, 0);
  const diffMs = delivery.getTime() - today.getTime();
  return Math.max(1, Math.ceil(diffMs / 86400000));
};

const INQUIRY_MESSAGE_MAX = 1000;
const INQUIRY_FILE_MAX_MB = 5;
const INQUIRY_FILE_TYPES = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

const formatInquiryDateLabel = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}-${m}-${y}`;
};

const buildInquiryFormDefaults = (product, unitLabel) => {
  const delivery = new Date();
  delivery.setDate(delivery.getDate() + 30);
  const pad = (n) => String(n).padStart(2, "0");
  const deliveryDate = `${delivery.getFullYear()}-${pad(delivery.getMonth() + 1)}-${pad(delivery.getDate())}`;
  const priceNum =
    parsePriceNumber(product?.price_amount) ||
    parsePriceNumber(product?.rate_label) ||
    parsePriceNumber(product?.price_label);
  const unit = unitLabel || "Pieces";
  return {
    quantity: product?.moq ? String(product.moq) : "",
    unit,
    targetPrice: product?.rate_label || (priceNum ? `₹${priceNum} / piece` : ""),
    deliveryDate,
    message: DEFAULT_INQUIRY_MESSAGE,
    attachment: null,
    attachmentName: "",
  };
};

function InquiryFieldLabel({ htmlFor, required, optional, info, children }) {
  const className = "mpd-inquiry-form__label";
  const content = (
    <>
      {required ? <span className="mpd-inquiry-form__req">*</span> : null}
      {children}
      {optional ? <span className="mpd-inquiry-form__optional"> (Optional)</span> : null}
      {info ? <FaInfoCircle className="mpd-inquiry-form__info" aria-hidden title={info} /> : null}
    </>
  );
  if (htmlFor) {
    return (
      <label className={className} htmlFor={htmlFor}>
        {content}
      </label>
    );
  }
  return <span className={className}>{content}</span>;
}

function SendInquiryModal({
  open,
  onClose,
  product,
  supplier,
  unitOptions,
  form,
  onChange,
  onSubmit,
  submitting,
}) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleFilePick = (file) => {
    if (!file) {
      onChange({ attachment: null, attachmentName: "" });
      return;
    }
    if (file.size > INQUIRY_FILE_MAX_MB * 1024 * 1024) {
      toast.error(`File must be ${INQUIRY_FILE_MAX_MB}MB or smaller`);
      return;
    }
    onChange({ attachment: file, attachmentName: file.name });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFilePick(e.dataTransfer.files?.[0] || null);
  };

  if (!open || !product) return null;

  const productTitle = product.title || "Product";
  const supplierName = supplier?.company_name || "Supplier";
  const moqLabel = product.moq_label || (product.moq ? `${product.moq} ${form.unit}` : "—");
  const rateLabel = product.rate_label || product.price_label || "—";
  const thumbSrc = product.image_path ? resolveHubImage(product.image_path) : null;
  const messageLen = String(form.message || "").length;
  const summaryQty = form.quantity ? `${form.quantity} ${form.unit}`.trim() : "—";
  const summaryPrice = form.targetPrice?.trim() || "—";
  const summaryDate = form.deliveryDate ? formatInquiryDateLabel(form.deliveryDate) : "—";
  const unitChoices = (() => {
    const base = unitOptions?.length ? [...unitOptions] : [];
    if (form.unit && !base.some((o) => o.label === form.unit)) {
      base.unshift({ value: form.unit, label: form.unit });
    }
    return base.length ? base : [{ value: form.unit, label: form.unit }];
  })();

  return (
    <div className="mpd-inquiry-modal" role="presentation" onClick={onClose}>
      <div
        className="mpd-inquiry-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mpd-inquiry-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="mpd-inquiry-modal__close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <header className="mpd-inquiry-modal__header">
          <div className="mpd-inquiry-modal__title-row">
            <span className="mpd-inquiry-modal__icon" aria-hidden>
              <FaPaperPlane />
            </span>
            <div>
              <h2 id="mpd-inquiry-title" className="mpd-inquiry-modal__title">
                Send Inquiry to Supplier
              </h2>
              <p className="mpd-inquiry-modal__subtitle">
                Fill the details below and send your inquiry to the supplier.
              </p>
            </div>
          </div>
        </header>

        <div className="mpd-inquiry-modal__body">
          <form id="mpd-inquiry-form" className="mpd-inquiry-form" onSubmit={onSubmit}>
            <div className="mpd-inquiry-form__product-card">
              {thumbSrc ? (
                <img src={thumbSrc} alt={productTitle} className="mpd-inquiry-form__product-thumb" />
              ) : (
                <div className="mpd-inquiry-form__product-thumb mpd-inquiry-form__product-thumb--empty">
                  <FaBox aria-hidden />
                </div>
              )}
              <div className="mpd-inquiry-form__product-meta">
                <p><strong>Product:</strong> {productTitle}</p>
                <p><strong>Supplier:</strong> {supplierName}</p>
                <p>
                  <strong>MOQ:</strong> {moqLabel}
                  <span className="mpd-inquiry-form__meta-sep">|</span>
                  <strong>Rate:</strong> {rateLabel}
                </p>
              </div>
            </div>

            <div className="mpd-inquiry-form__field">
              <InquiryFieldLabel htmlFor="mpd-inquiry-qty" required info="Enter the quantity you need">
                Quantity Required
              </InquiryFieldLabel>
              <div className="mpd-inquiry-form__qty-row">
                <input
                  id="mpd-inquiry-qty"
                  type="number"
                  min={1}
                  className="mpd-inquiry-form__input mpd-inquiry-form__input--qty"
                  value={form.quantity}
                  onChange={(e) => onChange({ quantity: e.target.value })}
                  required
                />
                <select
                  className="mpd-inquiry-form__input mpd-inquiry-form__input--unit"
                  value={form.unit}
                  onChange={(e) => onChange({ unit: e.target.value })}
                  aria-label="Quantity unit"
                >
                  {unitChoices.map((opt) => (
                    <option key={String(opt.value)} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mpd-inquiry-form__field">
              <InquiryFieldLabel htmlFor="mpd-inquiry-price" optional info="Your expected price per unit">
                Target Price
              </InquiryFieldLabel>
              <input
                id="mpd-inquiry-price"
                type="text"
                className="mpd-inquiry-form__input"
                value={form.targetPrice}
                onChange={(e) => onChange({ targetPrice: e.target.value })}
                placeholder="₹95 / piece"
              />
            </div>

            <div className="mpd-inquiry-form__field">
              <InquiryFieldLabel htmlFor="mpd-inquiry-date" required>
                Expected Delivery Date
              </InquiryFieldLabel>
              <div className="mpd-inquiry-form__date-field">
                <FaCalendarAlt className="mpd-inquiry-form__date-icon" aria-hidden />
                <span className="mpd-inquiry-form__date-text">
                  {formatInquiryDateLabel(form.deliveryDate) || "Select date"}
                </span>
                <input
                  id="mpd-inquiry-date"
                  type="date"
                  className="mpd-inquiry-form__date-input"
                  value={form.deliveryDate}
                  onChange={(e) => onChange({ deliveryDate: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mpd-inquiry-form__field">
              <InquiryFieldLabel htmlFor="mpd-inquiry-message" required info="Describe your requirements clearly">
                Message to Supplier
              </InquiryFieldLabel>
              <div className="mpd-inquiry-form__textarea-wrap">
                <textarea
                  id="mpd-inquiry-message"
                  className="mpd-inquiry-form__textarea"
                  rows={4}
                  maxLength={INQUIRY_MESSAGE_MAX}
                  value={form.message}
                  onChange={(e) => onChange({ message: e.target.value })}
                  required
                />
                <span className="mpd-inquiry-form__char-count">
                  {messageLen} / {INQUIRY_MESSAGE_MAX}
                </span>
              </div>
            </div>

            <div className="mpd-inquiry-form__field">
              <InquiryFieldLabel optional info="Attach drawings, specs, or reference documents">
                Attachment
              </InquiryFieldLabel>
              <input
                ref={fileInputRef}
                type="file"
                className="mpd-inquiry-form__file-input"
                accept={INQUIRY_FILE_TYPES}
                onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
              />
              <div
                className={`mpd-inquiry-form__dropzone${dragActive ? " is-active" : ""}${form.attachmentName ? " has-file" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDrop={onDrop}
              >
                <FaCloudUploadAlt className="mpd-inquiry-form__dropzone-icon" aria-hidden />
                <p className="mpd-inquiry-form__dropzone-title">
                  {form.attachmentName ? form.attachmentName : "Click to upload or drag and drop"}
                </p>
                <p className="mpd-inquiry-form__dropzone-hint">PDF, DOC, JPG or PNG (Max. 5MB)</p>
              </div>
            </div>
          </form>

          <aside className="mpd-inquiry-sidebar">
            <section className="mpd-inquiry-sidebar__block">
              <h3 className="mpd-inquiry-sidebar__heading">Inquiry Summary</h3>
              <ul className="mpd-inquiry-sidebar__list">
                <li><FaBox aria-hidden /><span><strong>Product:</strong> {productTitle}</span></li>
                <li><FaBuilding aria-hidden /><span><strong>Supplier:</strong> {supplierName}</span></li>
                <li><FaTag aria-hidden /><span><strong>MOQ:</strong> {moqLabel}</span></li>
                <li><FaRupeeSign aria-hidden /><span><strong>Rate:</strong> {rateLabel}</span></li>
              </ul>
            </section>

            <section className="mpd-inquiry-sidebar__block">
              <h3 className="mpd-inquiry-sidebar__heading">Your Inquiry</h3>
              <dl className="mpd-inquiry-sidebar__preview">
                <div>
                  <dt>Quantity</dt>
                  <dd>{summaryQty}</dd>
                </div>
                <div>
                  <dt>Target Price</dt>
                  <dd>{summaryPrice}</dd>
                </div>
                <div>
                  <dt>Delivery Date</dt>
                  <dd>{summaryDate}</dd>
                </div>
              </dl>
            </section>

            <div className="mpd-inquiry-sidebar__notice">
              <FaShieldAlt aria-hidden />
              <p>
                <strong>Your information is secure.</strong>{" "}
                Your inquiry details will be shared only with this supplier.
              </p>
            </div>

            <section className="mpd-inquiry-sidebar__tips">
              <h3 className="mpd-inquiry-sidebar__heading mpd-inquiry-sidebar__heading--tips">
                <FaLightbulb aria-hidden /> Tips
              </h3>
              <ul>
                <li>Be clear about your requirements.</li>
                <li>Mention target price for better responses.</li>
                <li>You can attach drawings or documents if any.</li>
              </ul>
            </section>
          </aside>
        </div>

        <footer className="mpd-inquiry-modal__footer">
          <button type="button" className="mpd-inquiry-form__cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="mpd-inquiry-form" className="mpd-inquiry-form__submit" disabled={submitting}>
            <FaPaperPlane aria-hidden />
            {submitting ? "Sending…" : "Send Inquiry"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ContactSupplierMini({
  contactName,
  supplier,
  miniMessage,
  onMiniMessageChange,
  onSend,
  onChat,
}) {
  return (
    <div className="mpd-contact-mini" id="mpd-contact-chat">
      <h3>Contact Supplier</h3>
      <div className="mpd-contact-mini__person">
        <div className="mpd-contact-mini__avatar"><FaUser aria-hidden /></div>
        <div>
          <strong>{contactName}</strong>
          {supplier?.designation ? <p>{supplier.designation}</p> : null}
        </div>
      </div>
      <button type="button" className="mpd-contact-mini__chat" onClick={onChat}>
        <FaComments aria-hidden /> Chat With Supplier
      </button>
      <textarea
        className="mpd-contact-mini__textarea"
        rows={4}
        placeholder="Enter between 20 to 4,000 characters."
        value={miniMessage}
        onChange={(e) => onMiniMessageChange(e.target.value)}
      />
      <button type="button" className="mpd-btn mpd-btn--primary mpd-btn--block" onClick={onSend}>
        Send
      </button>
    </div>
  );
}

function CompanySidebar({
  heading,
  navItems,
  activeNav,
  onNavChange,
  contactName,
  supplier,
  miniMessage,
  onMiniMessageChange,
  onSend,
  onChat,
}) {
  return (
    <aside className="mpd-company-nav">
      <h3 className="mpd-company-nav__heading">{heading}</h3>
      <ul>
        {navItems.map((label, idx) => (
          <li key={label}>
            <button
              type="button"
              className={activeNav === idx ? "is-active" : ""}
              onClick={() => onNavChange(idx)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
      <ContactSupplierMini
        contactName={contactName}
        supplier={supplier}
        miniMessage={miniMessage}
        onMiniMessageChange={onMiniMessageChange}
        onSend={onSend}
        onChat={onChat}
      />
    </aside>
  );
}

function DataTable({ columns, rows, emptyText = "No data available." }) {
  if (!rows?.length) return <p className="mpd-box__empty">{emptyText}</p>;
  return (
    <table className="mpd-data-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            {columns.map((col) => (
              <td key={col.key}>{row[col.key] ?? "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="mpd-box">
      {title ? <h3 className="mpd-box__title">{title}</h3> : null}
      <div className="mpd-box__body">{children}</div>
    </div>
  );
}

function CompanyOverviewPanel({ supplier, onBookTour }) {
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const overview = supplier?.company_overview || {};
  const logoUrl = overview.logo_url || supplier?.logo_url || null;
  const gallery = useMemo(() => {
    const items = (supplier?.gallery_images || []).filter(Boolean);
    if (items.length) return items;
    if (logoUrl) return [logoUrl];
    return [];
  }, [supplier?.gallery_images, logoUrl]);
  const description = overview.description || supplier?.company_description || "";
  const previewLen = 280;
  const showViewMore = description.length > previewLen;
  const descriptionText = descExpanded || !showViewMore
    ? description
    : `${description.slice(0, previewLen).trim()}…`;

  const prevGallery = () => setGalleryIdx((i) => (i <= 0 ? gallery.length - 1 : i - 1));
  const nextGallery = () => setGalleryIdx((i) => (i >= gallery.length - 1 ? 0 : i + 1));

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Company Overview</h3>
      <div className="mpd-co-card__top">
        <div className="mpd-co-carousel">
          <div className="mpd-co-carousel__stage">
            {gallery.length > 1 ? (
              <>
                <button type="button" className="mpd-co-carousel__nav mpd-co-carousel__nav--prev" onClick={prevGallery} aria-label="Previous image">
                  <FaChevronLeft aria-hidden />
                </button>
                <button type="button" className="mpd-co-carousel__nav mpd-co-carousel__nav--next" onClick={nextGallery} aria-label="Next image">
                  <FaChevronRight aria-hidden />
                </button>
              </>
            ) : null}
            {gallery.length ? (
              <img
                src={resolveHubImage(gallery[galleryIdx])}
                alt={supplier?.company_name || "Company"}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="mpd-co-carousel__empty">No image</div>
            )}
          </div>
          <button type="button" className="mpd-co-carousel__tour" onClick={onBookTour}>
            Book a Factory Tour
          </button>
        </div>
        <div className="mpd-co-summary">
          {logoUrl ? (
            <div className="mpd-co-profile">
              {!logoBroken ? (
                <img
                  className="mpd-co-profile__logo"
                  src={resolveHubImage(logoUrl)}
                  alt={`${supplier?.company_name || "Company"} logo`}
                  loading="lazy"
                  onError={() => setLogoBroken(true)}
                />
              ) : (
                <div className="mpd-co-profile__logo mpd-co-profile__logo--fallback" aria-hidden>
                  {(supplier?.company_name || "C").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          ) : null}
          {(overview.summary_rows || []).length ? (
            overview.summary_rows.map((row) => (
              <div key={row.label} className="mpd-co-summary__row">
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            ))
          ) : (
            <p className="mpd-box__empty">No company overview information available.</p>
          )}
        </div>
      </div>
      {overview.address ? (
        <div className="mpd-co-full-row">
          <span>Address</span>
          <strong>{overview.address}</strong>
        </div>
      ) : null}
      {overview.country_of_origin ? (
        <div className="mpd-co-full-row">
          <span>Country of Origin</span>
          <strong>{overview.country_of_origin}</strong>
        </div>
      ) : null}
      {description ? (
        <div className="mpd-co-description">
          <span className="mpd-co-description__label">Company Description</span>
          <p className="mpd-text">{descriptionText}</p>
          {showViewMore ? (
            <button type="button" className="mpd-co-description__more" onClick={() => setDescExpanded((v) => !v)}>
              {descExpanded ? "View less" : "View more"}
              <FaChevronDown className={descExpanded ? "is-flipped" : ""} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TradingInformationPanel({ supplier }) {
  const rows = supplier?.trading_information?.rows || [];
  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Trading Information</h3>
      {rows.length ? (
        <div className="mpd-co-trading">
          {rows.map((row) => (
            <div key={row.label} className="mpd-co-trading__row">
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="mpd-box__empty">No trading information available.</p>
      )}
    </div>
  );
}

function BankDetailsPanel({ supplier }) {
  const rows = useMemo(() => {
    const mapped = supplier?.bank_details?.rows || [];
    if (mapped.length) return mapped;

    const raw = supplier?.bank_details?.raw || supplier?.bank_details;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];

    const specs = [
      ["Account Holder Name", ["account_holder_name", "accountHolderName", "accountHolder"]],
      ["Account Number", ["account_number", "accountNumber"]],
      ["Bank Name", ["bank_name", "bankName"]],
      ["Branch Name", ["branch_name", "branchName"]],
      ["IFSC Code", ["ifsc_code", "ifscCode", "ifsc"]],
      ["SWIFT Code", ["swift_code", "swiftCode", "swift"]],
    ];

    return specs
      .map(([label, keys]) => {
        const value = keys.map((key) => raw[key]).find((val) => val != null && String(val).trim());
        return value ? { label, value: String(value).trim() } : null;
      })
      .filter(Boolean);
  }, [supplier]);

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Bank details</h3>
      {rows.length ? (
        <div className="mpd-co-trading">
          {rows.map((row) => (
            <div key={row.label} className="mpd-co-trading__row">
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="mpd-box__empty">No bank details available.</p>
      )}
    </div>
  );
}

function DetailRowsCard({ title, sections, rows, emptyText = "No information available." }) {
  const sectionList = sections?.length ? sections : null;
  const flatRows = rows?.length ? rows : null;

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">{title}</h3>
      {sectionList ? (
        sectionList.map((section) => (
          <div key={section.title} className="mpd-co-subsection">
            {sectionList.length > 1 ? <h4 className="mpd-co-subsection__title">{section.title}</h4> : null}
            {section.rows?.length ? (
              <div className="mpd-co-trading">
                {section.rows.map((row) => (
                  <div key={`${section.title}-${row.label}`} className="mpd-co-trading__row">
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mpd-box__empty">{emptyText}</p>
            )}
          </div>
        ))
      ) : flatRows ? (
        <div className="mpd-co-trading">
          {flatRows.map((row) => (
            <div key={row.label} className="mpd-co-trading__row">
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="mpd-box__empty">{emptyText}</p>
      )}
    </div>
  );
}

function FactoryDetailsContent({ supplier, activeNav }) {
  const tab = supplier?.factory_tab || {};
  if (activeNav === 1) {
    return (
      <DetailRowsCard
        title="Warehouse Location"
        sections={tab.warehouse_locations?.sections}
        emptyText="No warehouse location information available."
      />
    );
  }
  if (activeNav === 2) {
    return (
      <DetailRowsCard
        title="Supply Chain Logistics"
        rows={tab.supply_chain_logistics?.rows}
        emptyText="No supply chain logistics information available."
      />
    );
  }
  return (
    <DetailRowsCard
      title="Factory Details"
      sections={tab.factory_details?.sections}
      emptyText="No factory details available."
    />
  );
}

function QualityControlPanel({ supplier }) {
  const tab = supplier?.quality_tab?.quality_control || {};
  const description = tab.description || "";
  const rows = tab.rows || [];

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Quality Control</h3>
      {description ? (
        <div className="mpd-co-description">
          <p>{description}</p>
        </div>
      ) : null}
      {rows.length ? (
        <div className="mpd-co-trading">
          {rows.map((row) => (
            <div key={row.label} className="mpd-co-trading__row">
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {!description && !rows.length ? (
        <p className="mpd-box__empty">No quality control information available.</p>
      ) : null}
    </div>
  );
}

function CertificatesPanel({ supplier }) {
  const items = supplier?.quality_tab?.certificates?.items || [];

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Certificates</h3>
      {items.length ? (
        <div className="mpd-cert-list">
          {items.map((cert, idx) => (
            <div key={`${cert.name}-${idx}`} className="mpd-cert-card">
              <div className="mpd-cert-card__head">
                <h4>{cert.name}</h4>
                {cert.status ? (
                  <span className={`mpd-cert-card__status mpd-cert-card__status--${String(cert.status).toLowerCase()}`}>
                    {cert.status}
                  </span>
                ) : null}
              </div>
              {cert.issuer ? (
                <p><span>Issuer</span><strong>{cert.issuer}</strong></p>
              ) : null}
              {cert.date_issued ? (
                <p><span>Date Issued</span><strong>{cert.date_issued}</strong></p>
              ) : null}
              {cert.expiry_date ? (
                <p><span>Expiry Date</span><strong>{cert.expiry_date}</strong></p>
              ) : null}
              {cert.certificate_number ? (
                <p><span>Certificate Number</span><strong>{cert.certificate_number}</strong></p>
              ) : null}
              {cert.details ? (
                <p><span>Details</span><strong>{cert.details}</strong></p>
              ) : null}
              {cert.attachment_url ? (
                <p>
                  <span>Attachment</span>
                  <a href={resolveHubImage(cert.attachment_url)} target="_blank" rel="noopener noreferrer">
                    View Certificate
                  </a>
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mpd-box__empty">No certificates available.</p>
      )}
    </div>
  );
}

function QualityControlContent({ supplier, activeNav }) {
  if (activeNav === 1) return <CertificatesPanel supplier={supplier} />;
  return <QualityControlPanel supplier={supplier} />;
}

function RefundPolicyPanel({ supplier }) {
  const tab = supplier?.packaging_tab?.refund_policy || {};
  const description = tab.description || "";
  const rows = tab.rows || [];

  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">Refund Policy</h3>
      {rows.length ? (
        <div className="mpd-co-trading">
          {rows.map((row) => (
            <div key={row.label} className="mpd-co-trading__row">
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {description ? (
        <div className="mpd-co-description">
          <p>{description}</p>
        </div>
      ) : null}
      {!description && !rows.length ? (
        <p className="mpd-box__empty">No refund policy information available.</p>
      ) : null}
    </div>
  );
}

function PackagingDetailsContent({ supplier, activeNav }) {
  const tab = supplier?.packaging_tab || {};
  if (activeNav === 1) {
    return (
      <DetailRowsCard
        title="Accepted Payment Methods"
        rows={tab.accepted_payment_methods?.rows}
        emptyText="No payment method information available."
      />
    );
  }
  if (activeNav === 2) {
    return <RefundPolicyPanel supplier={supplier} />;
  }
  return (
    <DetailRowsCard
      title="Shipping Information"
      rows={tab.shipping_information?.rows}
      emptyText="No shipping information available."
    />
  );
}

function CompanyDetailsPlaceholder({ title }) {
  return (
    <div className="mpd-co-card">
      <h3 className="mpd-co-card__title">{title}</h3>
      <p className="mpd-box__empty">Content for this section will be available soon.</p>
    </div>
  );
}

function RelatedProductCard({ item }) {
  return (
    <Link to={`/marketplace/products/${item.id}`} className="mpd-popular-card">
      <div className="mpd-popular-card__media">
        {item.image_path ? (
          <img src={resolveHubImage(item.image_path)} alt={item.title} loading="lazy" />
        ) : (
          <div className="mpd-popular-card__no-image">No image</div>
        )}
      </div>
      <div className="mpd-popular-card__body">
        <h4>{item.title}</h4>
        {item.price_label ? <p className="mpd-popular-card__price">MRP {item.price_label}</p> : null}
        {item.moq_label ? <p className="mpd-popular-card__moq">{item.moq_label}</p> : null}
        <span className="mpd-popular-card__cta">Contact Now</span>
      </div>
    </Link>
  );
}

export default function ProductMarketplaceDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [activeCompanyMainTab, setActiveCompanyMainTab] = useState(0);
  const [activeCompanyNav, setActiveCompanyNav] = useState(0);
  const [activeFactoryNav, setActiveFactoryNav] = useState(0);
  const [activeQualityNav, setActiveQualityNav] = useState(0);
  const [activePackagingNav, setActivePackagingNav] = useState(0);
  const [miniMessage, setMiniMessage] = useState("");
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);
  const [messageForm, setMessageForm] = useState({ from: "", message: "" });
  const [viewLarger, setViewLarger] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState(() => buildInquiryFormDefaults(null, "Pieces"));
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [productEngagement, setProductEngagement] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProductEngagement(null);
    b2bHubHomeService
      .getProductDetail(productId)
      .then((res) => {
        if (cancelled) return;
        const payload =
          res?.success !== false && res?.data?.product
            ? res.data
            : res?.product
              ? res
              : null;
        if (!payload?.product) {
          setData(null);
          return;
        }
        setData(payload);
        if (payload.buyer_engagement?.has_submitted) {
          setProductEngagement(payload.buyer_engagement);
        }
        setActiveImage(0);
        setSelectedColor(payload.product?.colour_options?.[0]?.id ?? null);
        setSelectedSize(payload.product?.size_options?.[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.message || err?.response?.data?.error;
          toast.error(msg || "Failed to load product details");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    let cancelled = false;
    if (!productId || !StorageService.getToken() || productEngagement?.has_submitted) return undefined;

    CommonService.get(`/rfq/my/product/${productId}/engagement`, {}, { silent: true })
      .then((res) => {
        if (cancelled) return;
        const engagement = res?.data;
        if (engagement?.has_submitted) {
          setProductEngagement(engagement);
        }
      })
      .catch(() => {
        /* fallback only — primary signal comes from product detail API */
      });

    return () => {
      cancelled = true;
    };
  }, [productId, productEngagement?.has_submitted]);

  useEffect(() => {
    const user = StorageService.getUser();
    setMessageForm((prev) => ({ ...prev, from: user?.email || user?.user_email || "" }));
  }, []);

  const product = data?.product;
  const supplier = data?.supplier;
  const related = data?.related_products || [];
  const breadcrumb = data?.breadcrumb || [];

  const moqUnitLabel = useMemo(() => {
    const raw = product?.moq_unit;
    if (!raw) return "pieces";
    const match = attributeUnitOptions.find((o) => String(o.value) === String(raw));
    return match?.label || raw;
  }, [product?.moq_unit, attributeUnitOptions]);

  const galleryImages = useMemo(() => {
    const list = product?.gallery_images || [];
    return list.length ? list : product?.image_path ? [product.image_path] : [];
  }, [product]);

  const dimensionTableRow = useMemo(() => {
    if (!product?.dimensions_rows?.length) return [];
    const row = {};
    product.dimensions_rows.forEach((r) => {
      if (!r?.label) return;
      row[r.label.toLowerCase().replace(/\s+/g, "_")] = r.value;
    });
    return [row];
  }, [product?.dimensions_rows]);

  const goToCheckout = (extraMessage = "") => {
    if (productEngagement?.has_submitted) return;
    if (!StorageService.getToken()) {
      toast.info("Please sign in to place an order");
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    const url = new URL(`/marketplace/products/${product.id}/checkout`, window.location.origin);
    if (selectedColor != null) url.searchParams.set("color", String(selectedColor));
    if (selectedSize) url.searchParams.set("size", String(selectedSize));
    if (extraMessage.trim()) url.searchParams.set("message", extraMessage.trim());
    navigate(`${url.pathname}${url.search}`);
  };

  const prevImage = () => setActiveImage((i) => (i <= 0 ? galleryImages.length - 1 : i - 1));
  const nextImage = () => setActiveImage((i) => (i >= galleryImages.length - 1 ? 0 : i + 1));

  if (loading) {
    return (
      <div className="mpd-page">
        <B2BHubHomeHeader />
        <main className="mpd-main"><div className="mpd-loading">Loading product details…</div></main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mpd-page">
        <B2BHubHomeHeader />
        <main className="mpd-main">
          <p className="mpd-empty">Product not found.</p>
          <Link to="/marketplace/products">Back to marketplace</Link>
        </main>
      </div>
    );
  }

  const contactName = supplier?.contact_name || supplier?.company_name || "Supplier";
  const faqItems = product?.faq?.length ? product.faq : DEFAULT_PRODUCT_FAQ;

  const scrollToMessageForm = () => document.getElementById("mpd-message-form")?.scrollIntoView({ behavior: "smooth" });

  const openInquiryModal = () => {
    if (productEngagement?.has_submitted) return;
    setInquiryForm(buildInquiryFormDefaults(product, moqUnitLabel));
    setInquiryOpen(true);
  };

  const closeInquiryModal = () => {
    if (inquirySubmitting) return;
    setInquiryOpen(false);
  };

  const patchInquiryForm = (patch) => setInquiryForm((prev) => ({ ...prev, ...patch }));

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!StorageService.getToken()) {
      toast.info("Please sign in to send an enquiry");
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    if (!String(inquiryForm.quantity || "").trim()) {
      toast.error("Please enter quantity required");
      return;
    }
    if (!String(inquiryForm.message || "").trim()) {
      toast.error("Please enter your message");
      return;
    }
    const sellerCompanyId = supplier?.company_id;
    if (!sellerCompanyId) {
      toast.error("Unable to identify the product supplier. Please try again.");
      return;
    }

    setInquirySubmitting(true);
    try {
      let uploadedFiles = [];
      if (inquiryForm.attachment) {
        const formData = new FormData();
        formData.append("file", inquiryForm.attachment);
        formData.append("category", "product");
        formData.append("companyName", supplier?.company_name || "marketplace-inquiry");
        formData.append("fileType", "product_image");
        const uploadRes = await CommonService.post("/upload/single", formData, { silent: true });
        const fileUrl =
          uploadRes?.data?.accessPath || uploadRes?.data?.filePath || uploadRes?.data?.url || "";
        if (fileUrl) uploadedFiles = [fileUrl];
      }

      const targetPrice = parsePriceNumber(inquiryForm.targetPrice);
      const leadTimeDays = leadTimeDaysFromDate(inquiryForm.deliveryDate);
      const specialRequirements = [
        inquiryForm.deliveryDate ? `Expected delivery: ${inquiryForm.deliveryDate}` : null,
        inquiryForm.message.trim(),
      ]
        .filter(Boolean)
        .join("\n\n");

      const rfqPayload = {
        product_name: product.title,
        category_id: product.category_id ?? null,
        sub_category_id: product.subcategory_id ?? null,
        sub_sub_category_id: product.sub_subcategory_id ?? null,
        keywords: product.keywords || [],
        product_quantity: parseFloat(inquiryForm.quantity) || null,
        quantity_unit: inquiryForm.unit || product.moq_unit || null,
        target_price_min: targetPrice,
        target_price_max: targetPrice,
        preferences: "direct-product-inquiry",
        target_seller_company_id: Number(sellerCompanyId),
        source_product_id: Number(product.id),
        product_description: product.description || null,
        hsn_code: product.hsn_code || null,
        uploaded_files: uploadedFiles,
        lead_time_days: leadTimeDays,
        special_requirements: specialRequirements,
        status: "pending",
      };

      const response = await CommonService.post("/rfq", rfqPayload, { silent: true });
      if (response?.success) {
        setInquiryOpen(false);
        setProductEngagement({
          has_submitted: true,
          rfq_id: response?.data?.rfqId ?? response?.data?.rfq_id ?? null,
          preferences: "direct-product-inquiry",
          engagement_type: "enquiry",
        });
        toast.success("Enquiry sent to the supplier");
      } else {
        throw new Error(response?.message || "Failed to send enquiry");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to send enquiry";
      toast.error(msg);
    } finally {
      setInquirySubmitting(false);
    }
  };

  const scrollToChat = () => {
    setActiveTab("company");
    setActiveCompanyMainTab(0);
    setActiveCompanyNav(0);
    window.setTimeout(() => {
      document.getElementById("mpd-contact-chat")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const handleMiniMessageSend = () => {
    if (miniMessage.trim().length < 20) {
      toast.error("Please enter at least 20 characters");
      return;
    }
    goToCheckout(miniMessage);
  };

  const sidebarProps = {
    contactName,
    supplier,
    miniMessage,
    onMiniMessageChange: setMiniMessage,
    onSend: handleMiniMessageSend,
    onChat: scrollToChat,
  };

  const renderCompanyDetailsNavContent = () => {
    if (activeCompanyNav === 1) return <BankDetailsPanel supplier={supplier} />;
    if (activeCompanyNav === 2) return <TradingInformationPanel supplier={supplier} />;
    return (
      <CompanyOverviewPanel
        supplier={supplier}
        onBookTour={scrollToMessageForm}
      />
    );
  };

  const renderCompanyMainTabContent = () => {
    if (activeCompanyMainTab === 0) {
      return (
        <div className="mpd-company-section__grid">
          <CompanySidebar
            heading="Profile"
            navItems={COMPANY_DETAILS_NAV}
            activeNav={activeCompanyNav}
            onNavChange={setActiveCompanyNav}
            {...sidebarProps}
          />
          <div className="mpd-company-content">{renderCompanyDetailsNavContent()}</div>
        </div>
      );
    }
    if (activeCompanyMainTab === 1) {
      return (
        <div className="mpd-company-section__grid">
          <CompanySidebar
            heading="Profile"
            navItems={FACTORY_DETAILS_NAV}
            activeNav={activeFactoryNav}
            onNavChange={setActiveFactoryNav}
            {...sidebarProps}
          />
          <div className="mpd-company-content">
            <FactoryDetailsContent supplier={supplier} activeNav={activeFactoryNav} />
          </div>
        </div>
      );
    }
    if (activeCompanyMainTab === 2) {
      return (
        <div className="mpd-company-section__grid">
          <CompanySidebar
            heading="Profile"
            navItems={QUALITY_CONTROL_NAV}
            activeNav={activeQualityNav}
            onNavChange={setActiveQualityNav}
            {...sidebarProps}
          />
          <div className="mpd-company-content">
            <QualityControlContent supplier={supplier} activeNav={activeQualityNav} />
          </div>
        </div>
      );
    }
    if (activeCompanyMainTab === 3) {
      return (
        <div className="mpd-company-section__grid">
          <CompanySidebar
            heading="Profile"
            navItems={PACKAGING_DETAILS_NAV}
            activeNav={activePackagingNav}
            onNavChange={setActivePackagingNav}
            {...sidebarProps}
          />
          <div className="mpd-company-content">
            <PackagingDetailsContent supplier={supplier} activeNav={activePackagingNav} />
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCompanySection = () => (
    <div className="mpd-company-section mpd-company-section--in-tab" id="mpd-company-section">
      <h2 className="mpd-section-title">Company Details</h2>
      <div className="mpd-company-section__card">
        <div className="mpd-company-main-tabs">
          {COMPANY_MAIN_TABS.map((label, idx) => (
            <button
              key={label}
              type="button"
              className={activeCompanyMainTab === idx ? "is-active" : ""}
              onClick={() => {
                setActiveCompanyMainTab(idx);
                setActiveCompanyNav(0);
                setActiveFactoryNav(0);
                setActiveQualityNav(0);
                setActivePackagingNav(0);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {renderCompanyMainTabContent()}
      </div>
    </div>
  );

  const renderDescriptionTab = () => (
    <InfoBox title="Product Description">
      <p className="mpd-text">{product.description || "No description provided."}</p>
    </InfoBox>
  );

  const renderTabContent = () => {
    if (activeTab === "lead_time") {
      return (
        <InfoBox title="Lead Time">
          <DataTable
            columns={[
              { key: "lead_time_days", label: "Lead Time" },
              { key: "min_quantity", label: "Min. Quantity" },
              { key: "max_quantity", label: "Max. Quantity" },
            ]}
            rows={product.lead_time_rows}
            emptyText="No lead time information available."
          />
        </InfoBox>
      );
    }
    if (activeTab === "inventory") {
      return (
        <div className="mpd-tab-panels">
          <InfoBox title="Inventory Details">
            <DataTable
              columns={[
                { key: "available_quantity", label: "Available quantity" },
                { key: "unit", label: "Unit" },
                { key: "status", label: "Status" },
                { key: "stock_location", label: "Stock location" },
              ]}
              rows={product.inventory_rows}
              emptyText="No inventory information available."
            />
          </InfoBox>
          <InfoBox title="Dimensions">
            {dimensionTableRow.length ? (
              <DataTable
                columns={[
                  { key: "height", label: "Height" },
                  { key: "length", label: "Length" },
                  { key: "width", label: "Width" },
                  { key: "weight", label: "Weight" },
                  { key: "unit", label: "Unit" },
                ]}
                rows={dimensionTableRow}
              />
            ) : (
              <p className="mpd-box__empty">No dimension information available.</p>
            )}
          </InfoBox>
          <InfoBox title="Other Attributes">
            {product.attribute_rows?.length ? (
              <div className="mpd-attr-grid">
                {product.attribute_rows.map((row) => (
                  <div key={`${row.label}-${row.value}`} className="mpd-attr-grid__row">
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mpd-box__empty">No additional attributes available.</p>
            )}
          </InfoBox>
        </div>
      );
    }
    if (activeTab === "company") {
      return renderCompanySection();
    }
    return renderDescriptionTab();
  };

  const handleTopTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="mpd-page">
      <B2BHubHomeHeader />

      <main className="mpd-main">
        <nav className="mpd-breadcrumb" aria-label="Breadcrumb">
          {(breadcrumb.length ? breadcrumb : [{ label: product.title, href: null }]).map((crumb, idx) => (
            <React.Fragment key={`${crumb.label}-${idx}`}>
              {idx > 0 ? <span className="mpd-breadcrumb__sep">&gt;</span> : null}
              {crumb.href ? (
                <Link to={crumb.href}>{crumb.label}</Link>
              ) : (
                <span className={idx === breadcrumb.length - 1 ? "mpd-breadcrumb__current" : ""}>{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Hero — vertical thumbs | main image | product info */}
        <section className="mpd-hero">
          <div className="mpd-hero__gallery">
            <div className="mpd-hero__thumbs">
              {galleryImages.map((img, idx) => (
                <button
                  key={`${img}-${idx}`}
                  type="button"
                  className={`mpd-hero__thumb${activeImage === idx ? " is-active" : ""}`}
                  onClick={() => setActiveImage(idx)}
                >
                  <img src={resolveHubImage(img)} alt="" loading="lazy" />
                </button>
              ))}
            </div>
            <div className="mpd-hero__stage-wrap">
              <div className="mpd-hero__stage">
                {galleryImages.length > 1 ? (
                  <>
                    <button type="button" className="mpd-hero__nav mpd-hero__nav--prev" onClick={prevImage} aria-label="Previous image">
                      <FaChevronLeft aria-hidden />
                    </button>
                    <button type="button" className="mpd-hero__nav mpd-hero__nav--next" onClick={nextImage} aria-label="Next image">
                      <FaChevronRight aria-hidden />
                    </button>
                  </>
                ) : null}
                {galleryImages.length ? (
                  <img src={resolveHubImage(galleryImages[activeImage])} alt={product.title} />
                ) : (
                  <div className="mpd-hero__empty">No image</div>
                )}
              </div>
              {galleryImages.length ? (
                <button type="button" className="mpd-hero__view-larger" onClick={() => setViewLarger(true)}>
                  View Larger
                </button>
              ) : null}
            </div>
          </div>

          <div className="mpd-hero__info">
            <h1>{product.title}</h1>
            <div className="mpd-hero__seller">
              <strong>{supplier?.company_name || product.brand || "Seller"}</strong>
              <span className="mpd-hero__rating"><FaStar aria-hidden /> 4.5</span>
              {supplier?.location ? (
                <span className="mpd-hero__location"><FaMapMarkerAlt aria-hidden /> {supplier.location}</span>
              ) : null}
            </div>

            {product.moq ? (
              <p className="mpd-hero__line">
                <span>MOQ (Minimum Order Quantity):</span>
                <strong>{product.moq} {moqUnitLabel}</strong>
              </p>
            ) : null}
            {product.rate_label || product.price_label ? (
              <p className="mpd-hero__line">
                <span>Rate:</span>
                <strong>{product.rate_label || product.price_label}</strong>
              </p>
            ) : null}

            {product.bulk_discount_slabs?.length ? (
              <div className="mpd-bulk-horizontal">
                <p className="mpd-bulk-horizontal__title">Bulk Discount Slab</p>
                <div className="mpd-bulk-horizontal__grid">
                  {product.bulk_discount_slabs.map((slab) => (
                    <div key={slab.range_label} className="mpd-bulk-horizontal__cell">
                      <span className="mpd-bulk-horizontal__range">{slab.range_label}</span>
                      <strong className="mpd-bulk-horizontal__price">{slab.price_label}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {product.colour_options?.length ? (
              <div className="mpd-variant">
                <p className="mpd-variant__label">Colors ({product.colour_options.length})</p>
                <div className="mpd-variant__colors">
                  {product.colour_options.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`mpd-color${selectedColor === c.id ? " is-active" : ""}`}
                      style={{ backgroundColor: c.hex }}
                      title={c.label}
                      onClick={() => setSelectedColor(c.id)}
                      aria-label={c.label}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {product.size_options?.length ? (
              <div className="mpd-variant">
                <p className="mpd-variant__label">Size ({product.size_options.length})</p>
                <div className="mpd-variant__sizes">
                  {product.size_options.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={`mpd-size${selectedSize === size ? " is-active" : ""}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mpd-meta-inline">
              {product.model_number ? <div><span>Model No</span><strong>{product.model_number}</strong></div> : null}
              {product.hsn_code ? <div><span>HSN Code</span><strong>{product.hsn_code}</strong></div> : null}
              {product.tax_code ? <div><span>Tax Code</span><strong>{product.tax_code}</strong></div> : null}
              {product.keywords_label ? <div className="mpd-meta-inline__full"><span>Keywords</span><strong>{product.keywords_label}</strong></div> : null}
            </div>

            <div className="mpd-hero__actions">
              {productEngagement?.has_submitted ? (
                (() => {
                  const notice = getMarketplaceEngagementNotice(productEngagement);
                  return (
                    <p className="mpd-hero__submitted-note">
                      {notice.message}{" "}
                      <Link to={notice.linkTo}>{notice.linkLabel}</Link>
                    </p>
                  );
                })()
              ) : (
                <>
                  <button type="button" className="mpd-btn mpd-btn--outline" onClick={openInquiryModal}>
                    Send Enquiry
                  </button>
                  <MarketplaceAddToCartButton
                    item={product}
                    className="mpd-btn mpd-btn--outline"
                  />
                  <button type="button" className="mpd-btn mpd-btn--primary" onClick={() => goToCheckout()}>
                    Order Now
                  </button>
                </>
              )}
              <button type="button" className="mpd-btn mpd-btn--outline" onClick={scrollToChat}>
                Chat
              </button>
              <MarketplaceProductShareButton
                productId={product.id}
                title={product.title}
                sharePath={`/marketplace/products/${product.id}`}
                imagePath={galleryImages[0] || product.image_path || ""}
                companyName={supplier?.company_name || product.brand || ""}
                description={product.description || ""}
                menuPlacement="below"
                triggerClassName="mpd-btn mpd-btn--outline marketplace-product-share__trigger"
              />
            </div>
          </div>
        </section>

        {/* Tabs + sidebar */}
        <section className="mpd-detail-section">
          <div className="mpd-detail-section__main">
            <div className="mpd-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`mpd-tabs__btn${activeTab === tab.id ? " is-active" : ""}`}
                  onClick={() => handleTopTabClick(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {renderTabContent()}
          </div>

          <aside className="mpd-detail-section__sidebar">
            <div className="mpd-side-card">
              <h3>Supplier&apos;s Customization Ability</h3>
              {product.customization_items?.length ? (
                <ul className="mpd-checklist">
                  {product.customization_items.map((item, idx) => {
                    const label = typeof item === "string" ? item : item.label;
                    const detail = typeof item === "object" && item?.detail ? item.detail : null;
                    return (
                      <li key={`${label}-${idx}`}>
                        <FaCheck aria-hidden /> {label}
                        {detail ? <span className="mpd-checklist__detail">{detail}</span> : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mpd-box__empty">{product.customization_available ? "Customization available on request" : "Standard product only"}</p>
              )}
            </div>
            <div className="mpd-side-card">
              <h3>Shipping</h3>
              <p className="mpd-side-card__text">
                {product.shipping?.info_label || "Shipping fee varies by destination and quantity."}
              </p>
              {product.shipping?.shipping_terms ? (
                <p className="mpd-side-card__text"><strong>Shipping terms:</strong> {product.shipping.shipping_terms}</p>
              ) : null}
              {product.shipping?.mode_of_transport ? (
                <p className="mpd-side-card__text"><strong>Mode of transport:</strong> {product.shipping.mode_of_transport}</p>
              ) : null}
              {product.shipping?.dispatch_location ? (
                <p className="mpd-side-card__text"><strong>Dispatch location:</strong> {product.shipping.dispatch_location}</p>
              ) : null}
              {product.shipping?.lead_time_label ? (
                <p className="mpd-side-card__text"><strong>Est. transit:</strong> {product.shipping.lead_time_label}</p>
              ) : null}
            </div>
            <div className="mpd-side-card">
              <h3>Protection &amp; Policy</h3>
              {product.protection_policy?.payment_methods ? (
                <p className="mpd-side-card__text"><strong>Payment methods:</strong> {product.protection_policy.payment_methods}</p>
              ) : (
                <p className="mpd-side-card__text">Secure payments through the platform.</p>
              )}
              {product.protection_policy?.payment_schedule ? (
                <p className="mpd-side-card__text"><strong>Payment schedule:</strong> {product.protection_policy.payment_schedule}</p>
              ) : null}
              {product.protection_policy?.late_payment_policy ? (
                <p className="mpd-side-card__text"><strong>Late payment policy:</strong> {product.protection_policy.late_payment_policy}</p>
              ) : null}
              {product.protection_policy?.currency ? (
                <p className="mpd-side-card__text"><strong>Currency:</strong> {product.protection_policy.currency}</p>
              ) : null}
              {product.protection_policy?.refund_policy_available ? (
                <p className="mpd-side-card__text"><strong>Refund policy:</strong> {product.protection_policy.refund_policy_available}</p>
              ) : null}
              {product.protection_policy?.refund_policy_description ? (
                <p className="mpd-side-card__text">{product.protection_policy.refund_policy_description}</p>
              ) : null}
            </div>
          </aside>
        </section>

        {/* Popular products */}
        {related.length ? (
          <section className="mpd-popular">
            <div className="mpd-popular__head">
              <h2>Sellers Popular Products</h2>
              <div className="mpd-popular__nav">
                <button type="button" aria-label="Scroll left" onClick={() => document.getElementById("mpd-popular-track")?.scrollBy({ left: -300, behavior: "smooth" })}><FaChevronLeft aria-hidden /></button>
                <button type="button" aria-label="Scroll right" onClick={() => document.getElementById("mpd-popular-track")?.scrollBy({ left: 300, behavior: "smooth" })}><FaChevronRight aria-hidden /></button>
              </div>
            </div>
            <div className="mpd-popular__track" id="mpd-popular-track">
              {related.map((item) => <RelatedProductCard key={item.id} item={item} />)}
            </div>
          </section>
        ) : null}

        {/* FAQ + message */}
        <section className="mpd-bottom">
          <div className="mpd-bottom__faq">
            <h2 className="mpd-bottom__faq-title">Frequently Asked Questions</h2>
            <div className="mpd-bottom__faq-list">
              {faqItems.map((item, idx) => (
                <div key={`${item.question}-${idx}`} className={`mpd-faq-item${openFaq === idx ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className="mpd-faq-item__toggle"
                    onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    <span>{item.question}</span>
                    <FaChevronDown className="mpd-faq-item__icon" aria-hidden />
                  </button>
                  {openFaq === idx && item.answer ? <p className="mpd-faq-item__answer">{item.answer}</p> : null}
                </div>
              ))}
            </div>
          </div>
          <aside className="mpd-bottom__message" id="mpd-message-form">
            <h2 className="mpd-bottom__message-title">Send your message to this supplier</h2>
            <form
              className="mpd-msg-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!messageForm.message.trim()) {
                  toast.error("Please enter your message");
                  return;
                }
                goToCheckout(messageForm.message);
              }}
            >
              <div className="mpd-msg-field">
                <label htmlFor="mpd-msg-from"><span className="mpd-msg-field__req">*</span>From :</label>
                <input
                  id="mpd-msg-from"
                  type="email"
                  value={messageForm.from}
                  onChange={(e) => setMessageForm((p) => ({ ...p, from: e.target.value }))}
                  placeholder="buyeremail@example.com"
                />
              </div>
              <div className="mpd-msg-field">
                <label htmlFor="mpd-msg-to"><span className="mpd-msg-field__req">*</span>To :</label>
                <input id="mpd-msg-to" readOnly value={contactName} placeholder="Miss Hellen" />
              </div>
              <div className="mpd-msg-field mpd-msg-field--message">
                <label htmlFor="mpd-msg-body"><span className="mpd-msg-field__req">*</span>Message :</label>
                <textarea
                  id="mpd-msg-body"
                  rows={5}
                  value={messageForm.message}
                  onChange={(e) => setMessageForm((p) => ({ ...p, message: e.target.value }))}
                  placeholder="Enter Your message"
                />
              </div>
              <div className="mpd-msg-form__actions">
                <button type="submit" className="mpd-btn mpd-btn--primary">Send</button>
              </div>
            </form>
          </aside>
        </section>
      </main>

      <footer className="mpd-footer">
        <div className="mpd-footer__inner">
          <div className="mpd-footer__brand">
            <strong>TROCANET</strong>
            <p>A trusted B2B marketplace connecting buyers, sellers, manufacturers, logistics providers and quality inspectors worldwide.</p>
            <div className="mpd-footer__social">
              <FaTwitter aria-hidden /><FaLinkedin aria-hidden /><FaFacebook aria-hidden />
            </div>
          </div>
          <div className="mpd-footer__col"><h4>About Us</h4><Link to="/">Home</Link><Link to="/contact">Contact</Link></div>
          <div className="mpd-footer__col"><h4>For Buyers</h4><Link to="/marketplace/products">Products</Link><Link to="/buyer-rfq-form">Post RFQ</Link></div>
          <div className="mpd-footer__col"><h4>For Suppliers</h4><Link to="/signup">Register</Link><Link to="/seller-profile">Seller Profile</Link></div>
          <div className="mpd-footer__col"><h4>Customer Services</h4><Link to="/contact">Help Center</Link></div>
        </div>
        <div className="mpd-footer__bar">© {new Date().getFullYear()} Trocanet. All rights reserved.</div>
      </footer>

      {inquiryOpen ? (
        <SendInquiryModal
          open={inquiryOpen}
          onClose={closeInquiryModal}
          product={product}
          supplier={supplier}
          unitOptions={attributeUnitOptions}
          form={inquiryForm}
          onChange={patchInquiryForm}
          onSubmit={handleInquirySubmit}
          submitting={inquirySubmitting}
        />
      ) : null}

      {viewLarger && galleryImages.length ? (
        <div className="mpd-lightbox" role="dialog" onClick={() => setViewLarger(false)}>
          <div className="mpd-lightbox__content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="mpd-lightbox__close" onClick={() => setViewLarger(false)}>×</button>
            <img src={resolveHubImage(galleryImages[activeImage])} alt={product.title} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
